import React, { FC } from 'react';
import clsx from 'clsx';

interface ToastProps {
  title?: string;
  text?: string;
  variant?: 'info' | 'success' | 'warning' | 'critical';
  showCloseButton?: boolean;
  actions?: {
    label: string;
    onClick: () => void;
  }[];
  closeToast?: () => void;
}

const getVariantColor = (variant: ToastProps['variant']) => {
  switch (variant) {
    case 'info':
      return 'border-blue-400 text-blue-400';
    case 'success':
      return 'border-green-400 text-green-400';
    case 'warning':
      return 'border-yellow-400 text-yellow-400';
    case 'critical':
      return 'border-red-400 text-red-400';
    default:
      return '';
  }
};

const getVariantIcon = (variant: ToastProps['variant']) => {
  switch (variant) {
    case 'info':
      return 'ri-information-fill';
    case 'success':
      return 'ri-checkbox-circle-fill';
    case 'warning':
    case 'critical':
      return 'ri-error-warning-fill';
    default:
      return null;
  }
};

const Toast: FC<ToastProps> = ({
  title,
  text,
  showCloseButton,
  actions,
  variant,
  closeToast,
}) => {
  return (
    <div
      className={clsx(
        'w-[400px] rounded shadow flex p-3 bg-white border-l-4',
        getVariantColor(variant)
      )}
    >
      <div className="flex-1 flex-col justify-start items-start gap-2">
        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <i
              className={clsx(
                'text-h3 font-normal',
                getVariantIcon(variant),
                getVariantColor(variant)
              )}
            />
            <div className="text-navy-700 text-subtitle1a">{title}</div>
          </div>
          {showCloseButton && (
            <i
              className="ri-close-line text-h6 font-normal text-primary cursor-pointer"
              role="presentation"
              onClick={closeToast}
            />
          )}
        </div>
        {text && <div className="text-navy-700 text-body2">{text}</div>}
        {!!actions?.length && (
          <div className="flex gap-6">
            {actions.map((action, index) => (
              <button
                key={index}
                className="text-navy-700 text-subtitle3a cursor-pointer"
                type="button"
                onClick={action.onClick}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Toast;
